'use client';
/* eslint-disable */

import {
   Box,
   Button,
   Flex,
   Text,
   useColorModeValue,
   useDisclosure,
   Select,
   useBreakpointValue,
   useOutsideClick,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { createColumnHelper, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdNavigateNext, MdNavigateBefore } from 'react-icons/md';
import { motion } from 'framer-motion';
import { Input } from '@chakra-ui/react';
import { useUser } from 'contexts';
import MobileDataTable from '../../components/MobileDataTable';

// Components
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import StateMasterForm from './StateMasterForm';
import DeleteDialog from '../../components/DeleteDialog';
import DynamicTable from '../../components/DynamicAllDataTable';

// Actions
import { deleteState, getStates } from '../../../../../redux/actions/state';

// Utils
import { downloadPDF, downloadExcel } from '../../../../../utils/DataTableUtils';
import Loader from 'views/admin/default/Loader';

const columnHelper = createColumnHelper();
const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

export default function StateMaster({ columnsData }) {
   // Hooks and state
   const dispatch = useDispatch();
   const { isOpen, onOpen, onClose } = useDisclosure();
   const wrapperRef = useRef();

   // UI States
   const [sorting, setSorting] = useState([]);
   const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
   const [selectedRowId, setSelectedRowId] = useState(null);
   const [isEditMode, setIsEditMode] = useState(false);
   const [currentRow, setCurrentRow] = useState(null);
   const [expanded, setExpanded] = useState(false);

   // Pagination & Search
   const [searchQuery, setSearchQuery] = useState('');
   const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
   const [currentPage, setCurrentPage] = useState(1);
   const [limit, setLimit] = useState(10);
   const [isEditingPage, setIsEditingPage] = useState(false);
   const [pageInputValue, setPageInputValue] = useState('');
   const [isLoading, setIsLoading] = useState(false);

   // Selectors
   const stateData = useSelector((state) => state.state.list || []);
   const pagination = useSelector((state) => state.state?.pagination);

   // User permissions
   const { user } = useUser();
   const isAdmin = user?.isAdmin;
   const companyId = Number(localStorage.getItem('companyId'));
   const stateRight = Array.isArray(user?.userRights)
      ? user.userRights.find((right) => right.companyId === companyId)?.state
      : null;

   // Color modes
   const textColor = useColorModeValue('secondaryGray.900', 'white');
   const backgroundColor = useColorModeValue('white', 'rgba(12, 20, 41, 1)');
   const bg = useColorModeValue('#f2f7f2', '#1a254b');
   const icon = useColorModeValue('#000000', '#ffffff');

   // Responsive
   const isMobileOrTablet = useBreakpointValue({ base: true, md: false, lg: false });
   const isDesktop = useBreakpointValue({ base: false, md: true });

   // Column visibility
   const [columnVisibility, setColumnVisibility] = useState(
      columnsData.reduce((acc, column) => {
         acc[column.id] = true;
         return acc;
      }, {}),
   );

   // Memoized columns
   const columns = useMemo(() => {
      return columnsData
         .filter((column) => columnVisibility[column.id])
         .map((column) =>
            columnHelper.accessor(column.id, {
               id: column.id,
               header: () => (
                  <Text align="center" fontSize={{ sm: '10px', lg: '12px' }} color="gray.400">
                     {column.header}
                  </Text>
               ),
               cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
            }),
         );
   }, [columnsData, columnVisibility]);

   // Table instance
   const table = useReactTable({
      data: stateData,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      manualPagination: true,
   });

   const debounce = (func, delay) => {
      let timer;
      return function (...args) {
         clearTimeout(timer);
         timer = setTimeout(() => func.apply(this, args), delay);
      };
   };

   const debouncedSearchHandler = React.useCallback(
      debounce((query) => {
         setDebouncedSearchQuery(query);
      }, 500),
      [],
   );

   // Handle search input changes
   const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchQuery(value);
      debouncedSearchHandler(value);
   };

   // Fetch data from API
   const fetchData = useCallback(() => {
      const fetchMainData = async () => {
         try {
            setIsLoading(true);
            await dispatch(
               getStates({
                  page: currentPage,
                  // limit: limit === pagination?.totalProducts ? undefined : limit,
                  search: debouncedSearchQuery,
                  sortBy: sorting.length ? sorting[0]?.name : 'name',
                  sortOrder: sorting.length ? (sorting[0]?.desc ? 'desc' : 'asc') : 'asc',
               }),
            );
         } catch (error) {
            console.error('Error fetching main data:', error);
         } finally {
            setIsLoading(false);
         }
      };

      fetchMainData();
   }, [dispatch, currentPage, limit, debouncedSearchQuery, sorting, pagination?.totalProducts]);

   // Effect for fetching data when dependencies change
   useEffect(() => {
      fetchData();
   }, [fetchData]);

   // Reset page when search changes
   useEffect(() => {
      setCurrentPage(1);
   }, [searchQuery]);

   // Handle page navigation
   const handlePageChange = (directionOrPage) => {
      if (typeof directionOrPage === 'number') {
         // Direct page number navigation
         setCurrentPage(directionOrPage);
      } else {
         // Previous/next navigation
         const newPage = directionOrPage === 'next' ? pagination.nextPage : pagination.previousPage;
         if (newPage) setCurrentPage(newPage);
      }
   };
   const handlePageInputSubmit = () => {
      const page = parseInt(pageInputValue);
      if (!isNaN(page) && page >= 1 && page <= pagination?.totalPages) {
         handlePageChange(page);
      }
      setIsEditingPage(false);
      setPageInputValue('');
   };

   // Handle delete
   const handleDelete = useCallback(async () => {
      if (selectedRowId !== null) {
         try {
            await dispatch(deleteState(selectedRowId));
            fetchData();
            setDeleteDialogOpen(false);
         } catch (err) {
            // Error handling
         }
      }
   }, [selectedRowId, dispatch, fetchData]);

   // Handle delete click
   const handleDeleteClick = useCallback((id) => {
      setSelectedRowId(id);
      setDeleteDialogOpen(true);
   }, []);

   // Handle edit click
   const handleEditClick = useCallback(
      (row) => {
         setCurrentRow(row);
         setIsEditMode(true);
         onOpen();
      },
      [onOpen],
   );

   // Handle add new click
   const handleAddNew = useCallback(() => {
      setCurrentRow({});
      setIsEditMode(false);
      onOpen();
   }, [onOpen]);

   // Outside click handler for search
   useOutsideClick({
      ref: wrapperRef,
      handler: () => {
         if (searchQuery === '') setExpanded(false);
      },
   });

   return (
      <Box bg={backgroundColor} w="100%">
         <Flex
            width={{ base: '100%', md: 'auto' }}
            mb="8px"
            px={{ base: '1px', md: '25px' }}
            justifyContent="space-between"
            align="center"
            overflowX="auto"
            flexWrap="nowrap"
            gap={3}>
            {/* Left Section: Title + Add New Button */}
            <Flex align="center" gap={4} flexShrink={0}>
               <Text
                  color={textColor}
                  ml={{ base: '8px', md: 0 }}
                  fontSize={{ base: '17px', lg: '22px' }}
                  fontWeight="700"
                  lineHeight="100%">
                  States
               </Text>
               {/* {!isMobileOrTablet && (isAdmin || stateRight?.add) && (
                  <Button
                     onClick={handleAddNew}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}>
                     Add New
                  </Button>
               )} */}
            </Flex>

            {/* Right Section: Pagination + Limit + Search + Menu */}
            <Flex align="center" gap={2} flexShrink={0}>
               {/* Search Input */}
               <MotionFlex
                  ref={wrapperRef}
                  align="center"
                  bg={bg}
                  borderRadius="full"
                  me="20px"
                  cursor="pointer"
                  px={expanded ? 3 : 0}
                  w={expanded ? '200px' : '40px'}
                  h="40px"
                  transition="all 0.3s ease"
                  onClick={() => setExpanded(true)}>
                  <MotionSearchIcon
                     color={icon}
                     animate={{ rotate: expanded ? 0 : 90 }}
                     transition={{ duration: 0.3 }}
                     style={{ marginRight: expanded ? '8px' : '10px', marginLeft: expanded ? '0' : '10px' }}
                  />
                  {expanded && (
                     <Input
                        variant="unstyled"
                        placeholder="Search..."
                        value={searchQuery}
                        color={icon}
                        // onChange={(e) => setSearchQuery(e.target.value)}
                        onChange={handleSearchChange}
                        autoFocus
                     />
                  )}
               </MotionFlex>
            </Flex>

            {/* Mobile Floating Add New Button */}
            {/* {isMobileOrTablet && (isAdmin || stateRight?.add) && (
               <Box position="fixed" bottom="4" right="4" zIndex={999}>
                  <Button
                     onClick={handleAddNew}
                     variant="darkBrand"
                     color="white"
                     fontSize="sm"
                     fontWeight="500"
                     borderRadius="full"
                     p={{ base: '4', lg: '5' }}
                     boxShadow="lg">
                     Add New
                  </Button>
               </Box>
            )} */}
         </Flex>

         {/* Table */}
         {isLoading ? (
            <Loader />
         ) : (
            <DynamicTable
               right={stateRight}
               table={table}
               data={stateData}
               emptyMessage="No state data found"
               onEditClick={handleEditClick}
               onDeleteClick={handleDeleteClick}
               canDelete={true}
               columnVisibility={columnVisibility}
               pagename={false}
               columnsData={columnsData}
               sorting={sorting}
               limit={limit}
               setCurrentRow={setCurrentRow}
               onOpen={onOpen}
               setIsEditMode={setIsEditMode}
               setDeleteDialogOpen={setDeleteDialogOpen}
               setSelectedRowId={setSelectedRowId}
               debouncedSearchQuery={debouncedSearchQuery}
               type="state"
            />
         )}

         {/* Form Modal */}
         <StateMasterForm
            isOpen={isOpen}
            onClose={onClose}
            isEditMode={isEditMode}
            currentRow={currentRow}
            onSave={() => {
               fetchData();
            }}
         />

         {/* Delete Dialog */}
         <DeleteDialog
            isDeleteDialogOpen={isDeleteDialogOpen}
            setDeleteDialogOpen={setDeleteDialogOpen}
            handleDelete={handleDelete}
            title="State"
         />
      </Box>
   );
}
